import { useCallback, useEffect, useRef, useState } from 'react';
import { journeyStoryApi } from '../../api/journeyStoryApi';
import { useTravelText } from '../../app/TravelLocaleContext';
import {
  normalizeAuthorConsent,
  storyErrorKind,
  storyErrorMessageKey,
} from '../journey-publication/journeyStoryModel';

const K = 'travel.publication.story.consent';

/**
 * 回憶作者本人的公開故事收錄授權。
 * 公開回憶不等於已授權；撤回入口不受旅程狀態（完成／封存）限制。
 */
export default function MemoryStoryConsent({ journeyId, entry, canGrant = false }) {
  const generation = useRef(0);
  const busyRef = useRef(false);
  const [phase, setPhase] = useState('loading');
  const [consent, setConsent] = useState(null);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');
  const [pending, setPending] = useState(false);
  const memoryEntryId = entry.memoryEntryId;
  const { t } = useTravelText();

  const load = useCallback(async () => {
    const requestId = ++generation.current;
    try {
      const payload = await journeyStoryApi.getConsent(journeyId, memoryEntryId);
      if (requestId !== generation.current) return;
      setConsent(normalizeAuthorConsent(payload));
      setPhase('ready');
    } catch (cause) {
      if (requestId !== generation.current) return;
      setError(storyErrorMessageKey(storyErrorKind(cause), { context: 'consent' }));
      setPhase('error');
    }
  }, [journeyId, memoryEntryId]);

  useEffect(() => {
    setPhase('loading');
    setError('');
    setFeedback('');
    load();
    return () => {
      generation.current += 1;
    };
  }, [load]);

  async function change(granted) {
    if (busyRef.current) return;
    busyRef.current = true;
    const requestId = ++generation.current;
    setPending(true);
    setError('');
    setFeedback('');
    try {
      const payload = await journeyStoryApi.setConsent(journeyId, memoryEntryId, granted);
      if (requestId !== generation.current) return;
      setConsent(normalizeAuthorConsent(payload));
      setPhase('ready');
      setFeedback(granted ? `${K}.grantedFeedback` : `${K}.revokedFeedback`);
    } catch (cause) {
      if (requestId !== generation.current) return;
      const kind = storyErrorKind(cause);
      setError(storyErrorMessageKey(kind, { context: 'consent' }));
      if (kind !== 'network' && kind !== 'unauthorized') await load();
    } finally {
      busyRef.current = false;
      setPending(false);
    }
  }

  let body = null;
  if (phase === 'loading') {
    body = <p className="journey-journal__consent-text" aria-live="polite">{t(`${K}.loading`)}</p>;
  } else if (phase === 'ready' && consent && !consent.publicationExists) {
    body = (
      <p className="journey-journal__consent-text" data-consent-state="NO_PUBLICATION">
        {t(`${K}.noPublication`)}
      </p>
    );
  } else if (phase === 'ready' && consent?.authoredByOwner) {
    body = (
      <p className="journey-journal__consent-text" data-consent-state="OWNER">
        {t(`${K}.owner`)}
      </p>
    );
  } else if (phase === 'ready' && consent) {
    const granted = consent.authorConsent === 'GRANTED';
    let stateKey = `${K}.pending`;
    if (granted) stateKey = consent.ownerSelected ? `${K}.granted` : `${K}.grantedNotSelected`;
    else if (consent.authorConsent === 'REVOKED') stateKey = `${K}.revoked`;
    body = (
      <>
        <p className="journey-journal__consent-text" data-consent-state={consent.authorConsent}>
          {t(stateKey)}
        </p>
        {!entry.isPublic && !granted && canGrant ? (
          <p className="journey-journal__consent-text">{t(`${K}.needsPublic`)}</p>
        ) : null}
        <div className="journey-journal__actions">
          {granted ? (
            <button type="button" className="journey-journal__btn--revoke" disabled={pending} onClick={() => change(false)}>
              {pending ? t('travel.publication.story.updating') : t(`${K}.revoke`)}
            </button>
          ) : null}
          {!granted && canGrant ? (
            <button type="button" className="journey-journal__btn--primary" disabled={pending} onClick={() => change(true)}>
              {pending ? t('travel.publication.story.updating') : t(`${K}.grant`)}
            </button>
          ) : null}
        </div>
      </>
    );
  }

  return (
    <div className="journey-journal__consent" data-testid="memory-story-consent">
      {body}
      {error ? <p className="journey-journal__consent-error" role="alert">{t(error)}</p> : null}
      {feedback ? <p className="journey-journal__consent-text" role="status">{t(feedback)}</p> : null}
      {phase === 'error' ? (
        <div className="journey-journal__actions">
          <button type="button" onClick={() => { setError(''); setPhase('loading'); load(); }}>{t(`${K}.retry`)}</button>
        </div>
      ) : null}
    </div>
  );
}
